import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { Worker } from '../../engine/data';
import { AXIS_PROPS, GRID_PROPS, NO_ANIM, TOOLTIP_PROPS } from '../../chartTheme';
import { FBar, Panel, fedTone, toneText, toneVar } from '../../ui';
import { clamp100, computeOtResilience } from './otMath';

interface DotProps {
  cx?: number;
  cy?: number;
}

const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='mb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground'>{children}</div>
);

const SmallLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className='mb-1 text-[10px] uppercase tracking-wide text-muted-foreground'>{children}</div>
);

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <div className='flex items-center gap-1'>
    <span className='inline-block h-2 w-2 rounded-full opacity-60' style={{ background: color }} />
    <span className='text-muted-foreground'>{label}</span>
  </div>
);

/** Peer comparison (OT hours vs FED) and the worker's own hours-vs-FED response. */
export const OtResilience: React.FC<{ w: Worker }> = ({ w }) => {
  const o = useMemo(() => computeOtResilience(w), [w]);
  const toneColor = toneVar[o.tone];
  const peerData = o.peers.filter((p) => p.init !== w.init).map((p) => ({ x: p.otHrs, y: p.fed, init: p.init }));
  const selfData = [{ x: w.otHrs, y: w.fed, init: w.init }];
  const lastIdx = o.personal.length - 1;
  const personalData = o.personal.map((p) => ({ x: p.hrs, y: p.fed, wk: p.wk }));
  const fy = (h: number) => clamp100(o.pSlope * h + o.pIntercept);

  return (
    <Panel
      title='OT Resilience'
      sub='FED response to hours worked — peer comparison & personal schedule sensitivity'
      actions={
        o.hasOT && (
          <div className='text-right'>
            <div className={`text-[11px] font-bold ${toneText[o.tone]}`}>{o.label}</div>
            <div className='mt-0.5 text-[10px] text-muted-foreground'>
              {w.otHrs}h OT · FED {w.fed}
            </div>
          </div>
        )
      }
    >
      <div className='flex flex-wrap items-start gap-5'>
        <div className='min-w-[260px] flex-1'>
          <Caption>Peer comparison — OT hours vs FED this week</Caption>
          <div className='h-44'>
            <ResponsiveContainer width='100%' height='100%'>
              <ScatterChart margin={{ top: 12, right: 12, bottom: 18, left: -8 }}>
                <CartesianGrid {...GRID_PROPS} />
                <XAxis
                  type='number'
                  dataKey='x'
                  domain={[0, o.maxOT]}
                  unit='h'
                  label={{
                    value: 'OT Hours (this week)',
                    position: 'insideBottom',
                    offset: -10,
                    fontSize: 10,
                    fill: 'var(--color-muted-foreground)',
                  }}
                  {...AXIS_PROPS}
                />
                <YAxis
                  type='number'
                  dataKey='y'
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  label={{
                    value: 'FED',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 14,
                    fontSize: 10,
                    fill: 'var(--color-muted-foreground)',
                  }}
                  {...AXIS_PROPS}
                />
                <Tooltip {...TOOLTIP_PROPS} cursor={{ strokeDasharray: '3 3' }} />
                {o.trend && (
                  <ReferenceLine
                    segment={[
                      { x: o.trend.x1, y: o.trend.y1 },
                      { x: o.trend.x2, y: o.trend.y2 },
                    ]}
                    stroke='var(--color-muted-foreground)'
                    strokeDasharray='3 3'
                    strokeOpacity={0.5}
                  />
                )}
                <Scatter name='Peers' data={peerData} {...NO_ANIM}>
                  {peerData.map((p) => (
                    <Cell
                      key={p.init}
                      fill={p.y >= 80 ? toneVar.bad : p.y >= 60 ? toneVar.warn : toneVar.muted}
                      fillOpacity={0.35}
                    />
                  ))}
                </Scatter>
                <Scatter
                  name={w.init}
                  data={selfData}
                  fill={toneColor}
                  {...NO_ANIM}
                  shape={(p: DotProps) => (
                    <g>
                      <circle cx={p.cx} cy={p.cy} r={6} fill={toneColor} fillOpacity={0.9} />
                      <text x={(p.cx ?? 0) + 8} y={(p.cy ?? 0) + 4} fontSize={9} fontWeight={600} fill={toneColor}>
                        {w.init}
                      </text>
                    </g>
                  )}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className='flex w-40 shrink-0 flex-col gap-3 pt-[18px]'>
          {o.hasOT ? (
            <>
              <div>
                <SmallLabel>Resilience Index</SmallLabel>
                <div className='flex items-center gap-2'>
                  <FBar value={o.resilScore ?? 0} tone={o.tone} className='h-2 flex-1' />
                  <span className={`text-[13px] font-bold ${toneText[o.tone]}`}>{o.resilScore}</span>
                </div>
              </div>
              <div>
                <SmallLabel>FED per OT hour</SmallLabel>
                <div className='flex gap-3'>
                  <div>
                    <div className={`text-lg font-bold ${toneText[o.tone]}`}>{o.workerRate.toFixed(1)}</div>
                    <div className='text-[10px] text-muted-foreground'>{o.first}</div>
                  </div>
                  <div>
                    <div className='text-lg font-bold text-muted-foreground'>{o.avgPeerRate.toFixed(1)}</div>
                    <div className='text-[10px] text-muted-foreground'>Team avg</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='pt-2 text-xs text-muted-foreground'>No overtime worked this period.</div>
          )}
          <div className='border-t border-border pt-2.5 text-[11px] leading-normal text-muted-foreground'>{o.desc}</div>
        </div>
      </div>

      <div className='mt-1 border-t border-border pt-3.5'>
        <div className='flex flex-wrap items-start gap-5'>
          <div className='min-w-[260px] flex-1'>
            <Caption>Personal schedule response — {o.first}&apos;s hours vs FED (12 weeks)</Caption>
            <div className='h-44'>
              <ResponsiveContainer width='100%' height='100%'>
                <ScatterChart margin={{ top: 12, right: 14, bottom: 18, left: -8 }}>
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis
                    type='number'
                    dataKey='x'
                    domain={[o.minH, o.maxH]}
                    tickFormatter={(v: number) => `${Math.round(v)}h`}
                    label={{
                      value: 'Hours worked that week',
                      position: 'insideBottom',
                      offset: -10,
                      fontSize: 10,
                      fill: 'var(--color-muted-foreground)',
                    }}
                    {...AXIS_PROPS}
                  />
                  <YAxis
                    type='number'
                    dataKey='y'
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    label={{
                      value: 'FED',
                      angle: -90,
                      position: 'insideLeft',
                      offset: 14,
                      fontSize: 10,
                      fill: 'var(--color-muted-foreground)',
                    }}
                    {...AXIS_PROPS}
                  />
                  <Tooltip {...TOOLTIP_PROPS} cursor={{ strokeDasharray: '3 3' }} />
                  <ReferenceLine
                    segment={[
                      { x: o.minH, y: fy(o.minH) },
                      { x: o.maxH, y: fy(o.maxH) },
                    ]}
                    stroke={toneVar[o.slopeTone]}
                    strokeWidth={1.5}
                    strokeDasharray='4 3'
                    strokeOpacity={0.7}
                  />
                  <Scatter
                    name='Week'
                    data={personalData}
                    {...NO_ANIM}
                    shape={(p: DotProps & { payload?: { y: number; wk: number } }) => {
                      const col = toneVar[fedTone(p.payload?.y ?? 0)];
                      const latest = p.payload?.wk === lastIdx + 1;
                      return latest ? (
                        <g>
                          <circle cx={p.cx} cy={p.cy} r={6} fill={col} fillOpacity={0.95} />
                          <text x={(p.cx ?? 0) + 8} y={(p.cy ?? 0) + 4} fontSize={9} fontWeight={600} fill={col}>
                            now
                          </text>
                        </g>
                      ) : (
                        <circle cx={p.cx} cy={p.cy} r={3.5} fill={col} fillOpacity={0.55} />
                      );
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className='flex w-40 shrink-0 flex-col gap-2.5 pt-[18px]'>
            <div>
              <SmallLabel>Schedule Sensitivity</SmallLabel>
              <div className={`text-[13px] font-bold ${toneText[o.slopeTone]}`}>{o.slopeLabel}</div>
              <div className='mt-1 text-[10px] text-muted-foreground'>+{o.pSlope.toFixed(1)} FED per extra hour</div>
            </div>
            <div className='flex flex-wrap gap-2.5 text-[10px]'>
              <LegendDot color={toneVar.good} label='FED <60' />
              <LegendDot color={toneVar.warn} label='FED 60–79' />
              <LegendDot color={toneVar.bad} label='FED ≥80' />
            </div>
            <div className='border-t border-border pt-2.5 text-[11px] leading-normal text-muted-foreground'>
              Each dot is one week. The dashed line shows how {o.first}&apos;s FED tends to move as their hours change —
              steeper = more hours-sensitive.
            </div>
          </div>
        </div>
      </div>
    </Panel>
  );
};
